import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DealershipLink from './DealershipLink';
import Content from '../Content';
import SearchDealer from './SearchDealer';
import Logo from '../../assets/images/ipacket_logo.png';
import './dealerships.scss';
import { getUserCheck } from '../../actions/apiGetInfo';
import { setCurrentDealership, setDealership, resetRefresh, resetNav, resetAdmin, resetSite } from '../../actions/apiSetInfo';
import { logout } from '../../actions/authentication';
import { vsearch } from '../../actions/lookups';

const Dealerships = () => {
  const dispatch = useDispatch();
  const { vinToken } = useParams();

  const state = useSelector((state) => state);

  // Redux state
  const currentDealership = useSelector((state) => state.settings_dealerships.current_dealership);
  const dealerships = useSelector((state) => state.settings_dealerships.set_dealerships || []);
  const searchedDealerships = useSelector((state) => state.settings_dealerships.set_searched_dealerships || []);
  const authentication = useSelector((state) => state.authentication);
  const isLoading = useSelector((state) => state.settings_dealerships.isLoading || false);
  const currentUser = useSelector((state) => state.current_user);

  // Local state
  const [subdomain, setSubdomain] = useState('');
  const [vin, setVin] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [view, setView] = useState('active'); // Default to 'active'
  const [showSuperadminSettings, setShowSuperadminSettings] = useState(false);

  // Parse vinToken
  useEffect(() => {
    if (vinToken) {
      const [subdomainFromUrl, vinFromUrl] = vinToken.split(':');
      setSubdomain(subdomainFromUrl || '');
      setVin(vinFromUrl || '');
    }
  }, [vinToken]);

  // Handle VIN search and single-dealership selection
  useEffect(() => {
    if (vin) {
      dispatch(vsearch(vin, true));
    }

    if (dealerships.length === 1 && !currentDealership) {
      const timeout = setTimeout(() => {
        const { database, subdomain, id } = dealerships[0];
        dispatch(setCurrentDealership(database, subdomain, id));
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [vin, dealerships, currentDealership, dispatch]);

  // Periodic user check
  useEffect(() => {
    const interval = setInterval(() => {
      if (authentication?.access_token) {
        dispatch(getUserCheck());
      }
    }, 150000);
    return () => clearInterval(interval);
  }, [authentication, dispatch]);

  // Mark initial load complete
  useEffect(() => {
    setIsInitialLoad(false);
  }, []);

  // Logout handler
  const handleLogout = () => {
    logout(true);
  };

  // Handle view change
  const handleViewChange = (newView) => {
    setView(newView);
  };

  // Toggle superadmin settings visibility
  const toggleSuperadminSettings = () => {
    setShowSuperadminSettings((prev) => !prev);
  };

  // Memoized dealership rendering with filtering
  const displayDealerships = useMemo(() => {
    let targetDealerships = searchedDealerships.length > 0 ? searchedDealerships : dealerships;

    // Apply view filter
    targetDealerships = targetDealerships.filter((installation) => {
      if (view === 'active') return installation.active === true;
      if (view === 'dealerships') return installation.demo !== true && installation.active === true;
      if (view === 'demo') return installation.demo === true;
      if (view === 'deactivated') return !!installation.deactivated_date;
      return true; // Fallback (should not occur)
    });

    return targetDealerships.map((installation) => (
      <DealershipLink
        dealership={installation}
        key={`dealership_${installation.id}`}
        showSuperadminSettings={showSuperadminSettings}
      />
    ));
  }, [dealerships, searchedDealerships, view, showSuperadminSettings]);

  if (isInitialLoad || isLoading) {
    return (
      <div className="dealership-search-page">
        <div className="dealership-search-page-body">
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mobile-pad-left-2 mobile-pad-right-2 dealerpage dealership-search-page">
      {!currentDealership && (
        <>
          <div className="row navbar-light sticky-top dealership-search-page-header" style={{ background: 'white' }}>
            <div className="col-sm pad-top-1 pad-bottom-1 mobile-pad-left-1 dealership-search-page-header-section dealership-search-page-header-section-left">
              <img key="toplogo" src={Logo} style={{ marginTop: 5 }} height={50} alt="iPacket Logo" />
            </div>
            <div className="col-sm pad-top-1 pad-bottom-1 dealership-search-page-header-section dealership-search-page-header-section-middle">
              <SearchDealer />
            </div>
            <div className="col-sm pad-top-1 pad-bottom-1 dealership-search-page-header-section dealership-search-page-header-section-right">
              <div className="btn btn-md float-end btn-secondary" onClick={handleLogout}>
                <i className="far fa-power-off" /> Logout
              </div>
            </div>
          </div>
          {currentUser.role === 'superadmin' && (
            <div className="superadmin-toggle" style={{ padding: '10px' }}>
              <button
                className={`btn ${showSuperadminSettings ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={toggleSuperadminSettings}
              >
                {showSuperadminSettings ? 'Hide Superadmin Settings' : 'Show Superadmin Settings'}
              </button>
            </div>
          )}
          {currentUser.role === 'superadmin' && showSuperadminSettings && (
            <div className="view-toggle-buttons" style={{ padding: '10px', display: 'flex', gap: '10px' }}>
              <button
                className={`btn ${view === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => handleViewChange('active')}
              >
                Active
              </button>
              <button
                className={`btn ${view === 'dealerships' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => handleViewChange('dealerships')}
              >
                Dealerships
              </button>
              <button
                className={`btn ${view === 'demo' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => handleViewChange('demo')}
              >
                Demo
              </button>
              <button
                className={`btn ${view === 'deactivated' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => handleViewChange('deactivated')}
              >
                Deactivated
              </button>
            </div>
          )}
        </>
      )}
      {currentDealership ? <Content /> : <div className="dealership-search-page-body">{displayDealerships}</div>}
    </div>
  );
};

export default Dealerships;